import {
	computed,
	ComputedRef,
	Ref,
} from '@vue/composition-api';
import { Group } from '@front-app/services/GroupsApi/GroupModel';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import {
	canViewDataDashboardPageForGroup,
	canViewEnhancedDashboard,
	canViewGroupSettingsPage,
	canViewGroupConfigurationPage,
	canViewGroupHighlightsAdminPage,
	canViewGroupAdminCustomPage,
	canViewCustomTab,
	canViewGroupOtherSettingsPage,
	canViewLearningProgramsAdminPage,
	canViewMemberManagementPage,
	canSeeAtLeastOneMemberList,
	canInviteMembers,
	canViewInviteCampaignTab,
	canViewGroupNotificationSettingsPage,
	canViewGroupDigestManagementPage,
	canCreateGroupEvents,
	canViewLearningProgramsSurveyResultsPage,
	canViewLearningProgramsManagementPage,
} from '@front-app/util/permission';

export enum MenuOptions {
	SECURITY_AND_ACCESS_CONTROL = 'security_and_access_settings',
	MEMBER_MANAGEMENT = 'member_management',
	DESIGN_AND_CONFIGURATION = 'design_and_configuration',
	NOTIFICATIONS_AND_DIGESTS = 'notifications_and_digests',
	PINNED_HIGHLIGHTS = 'pinned_highlights',
	DATA_DASHBOARD = 'data_dashboard',
	LEARNING_PROGRAM = 'learning_program',
	LEARNING_PROGRAM_SURVEYS = 'learning_program_surveys',
	CONFIGURATION = 'configuration',
	CUSTOM_PAGE = 'custom_page',
	OTHER_SETTINGS = 'other_settings',
}

const canAccessDesignAndConfiguration = (group: Group | undefined, isUserCsr: boolean) => {
	return canViewGroupHighlightsAdminPage(group, isUserCsr) || canViewGroupConfigurationPage(group, isUserCsr);
};

const canAccessAllLms = (group: Group | undefined, isUserCsr: boolean) => canViewLearningProgramsSurveyResultsPage(group, isUserCsr)
	&& canViewLearningProgramsManagementPage(group, isUserCsr);

const canAccessOnlyLmsSurveys = (group: Group | undefined, isUserCsr: boolean) => canViewLearningProgramsSurveyResultsPage(group, isUserCsr)
	&& !canViewLearningProgramsManagementPage(group, isUserCsr);

const MENU_OPTION_VISIBILITY = {
	[MenuOptions.SECURITY_AND_ACCESS_CONTROL]: canViewGroupSettingsPage,
	[MenuOptions.MEMBER_MANAGEMENT]: canViewMemberManagementPage,
	[MenuOptions.DESIGN_AND_CONFIGURATION]: canAccessDesignAndConfiguration,
	[MenuOptions.NOTIFICATIONS_AND_DIGESTS]: canViewGroupNotificationSettingsPage || canViewGroupDigestManagementPage,
	[MenuOptions.PINNED_HIGHLIGHTS]: canViewGroupHighlightsAdminPage,
	[MenuOptions.DATA_DASHBOARD]: canViewDataDashboardPageForGroup,
	[MenuOptions.LEARNING_PROGRAM]: canAccessAllLms,
	[MenuOptions.LEARNING_PROGRAM_SURVEYS]: canAccessOnlyLmsSurveys,
	[MenuOptions.CONFIGURATION]: canViewGroupConfigurationPage,
	[MenuOptions.CUSTOM_PAGE]: canViewGroupAdminCustomPage,
	[MenuOptions.OTHER_SETTINGS]: canViewGroupOtherSettingsPage,
};

export interface UseAdminAccess {
	menuOptions: ComputedRef<MenuOptions[]>;
	hasAdminPageAccess: ComputedRef<boolean>;
	hasMemberManagementAccess: ComputedRef<boolean>;
	hasMembersListAccess: ComputedRef<boolean>;
	hasAddMembersAccess: ComputedRef<boolean>;
	hasInviteCampaignAccess: ComputedRef<boolean>;
	hasSecuritySettingsAccess: ComputedRef<boolean>;
	hasAnyDesignAndConfigurationAccess: ComputedRef<boolean>;
	hasAccessToMoreThanOneDesignAndConfigurationTab: ComputedRef<boolean>;
	hasAnyNotificationsAndDigestsAccess: ComputedRef<boolean>;
	hasAllNotificationsAndDigestsAccess: ComputedRef<boolean>;
	hasAnyLearningProgramsAccess: ComputedRef<boolean>;
	hasAllLearningProgramsManagementAccess: ComputedRef<boolean>;
	hasOnlyLearningProgramsSurveysViewAccess: ComputedRef<boolean>;
	hasConfigurationAccess: ComputedRef<boolean>;
	hasDataDashboardAccess: ComputedRef<boolean>;
	hasHighlightsAccess: ComputedRef<boolean>;
	hasCustomPageAccess: ComputedRef<boolean>;
	hasNotificationSettingsAccess: ComputedRef<boolean>;
	hasDigestManagementAccess: ComputedRef<boolean>;
	hasEventManagementAccess: ComputedRef<boolean>;
	isFreeBeta(key: MenuOptions): boolean;
}

export const useAdminAccess = (group: Ref<Group | undefined>): UseAdminAccess => {
	const { isCsr } = CurrentUserStore;

	const menuOptions = computed(() => Object.values(MenuOptions)
		.filter((key) => MENU_OPTION_VISIBILITY[key](group.value, isCsr.value)));

	const hasAdminPageAccess = computed(() => menuOptions.value.length > 0);

	const hasSecuritySettingsAccess = computed(() => canViewGroupSettingsPage(group.value, isCsr.value));
	const hasMemberManagementAccess = computed(() => canViewMemberManagementPage(group.value, isCsr.value));
	const hasMembersListAccess = computed(() => canSeeAtLeastOneMemberList(group.value));
	const hasAddMembersAccess = computed(() => canInviteMembers(group.value));
	const hasInviteCampaignAccess = computed(() => canViewInviteCampaignTab(group.value, isCsr.value));
	const hasAnyDesignAndConfigurationAccess = computed(() => {
		return canViewGroupHighlightsAdminPage(group.value, isCsr.value)
			|| canViewGroupConfigurationPage(group.value, isCsr.value)
			|| canViewCustomTab(group.value);
	});
	const hasAccessToMoreThanOneDesignAndConfigurationTab = computed(() => {
		let tabs = 0;

		if (canViewGroupHighlightsAdminPage(group.value, isCsr.value)) {
			tabs += 1;
		}
		if (canViewGroupConfigurationPage(group.value, isCsr.value)) {
			tabs += 1;
		}
		if (canViewCustomTab(group.value)) {
			tabs += 1;
		}

		return tabs > 1;
	});
	const hasAnyNotificationsAndDigestsAccess = computed(() => {
		return canViewGroupNotificationSettingsPage(group.value) || canViewGroupDigestManagementPage(group.value, isCsr.value);
	});
	const hasAllNotificationsAndDigestsAccess = computed(() => {
		return canViewGroupNotificationSettingsPage(group.value) && canViewGroupDigestManagementPage(group.value, isCsr.value);
	});
	const hasAnyLearningProgramsAccess = computed(() => canViewLearningProgramsAdminPage(group.value, isCsr.value));
	const hasAllLearningProgramsManagementAccess = computed(() => canAccessAllLms(group.value, isCsr.value));
	const hasOnlyLearningProgramsSurveysViewAccess = computed(() => canAccessOnlyLmsSurveys(group.value, isCsr.value));
	const hasConfigurationAccess = computed(() => canViewGroupConfigurationPage(group.value, isCsr.value));
	const hasDataDashboardAccess = computed(() => canViewDataDashboardPageForGroup(group.value, isCsr.value));
	const hasHighlightsAccess = computed(() => canViewGroupHighlightsAdminPage(group.value, isCsr.value));
	const hasCustomPageAccess = computed(() => canViewGroupAdminCustomPage(group.value, isCsr.value));
	const hasNotificationSettingsAccess = computed(() => canViewGroupNotificationSettingsPage(group.value));
	const hasDigestManagementAccess = computed(() => canViewGroupDigestManagementPage(group.value, isCsr.value));
	const hasEventManagementAccess = computed(() => canCreateGroupEvents(CurrentUserStore.isCsr.value, group.value));

	const isFreeBeta = (key: MenuOptions) => (key === MenuOptions.DATA_DASHBOARD && !canViewEnhancedDashboard(group.value));

	return {
		menuOptions,
		hasAdminPageAccess,
		hasMemberManagementAccess,
		hasAddMembersAccess,
		hasInviteCampaignAccess,
		hasMembersListAccess,
		hasSecuritySettingsAccess,
		hasAnyDesignAndConfigurationAccess,
		hasAccessToMoreThanOneDesignAndConfigurationTab,
		hasAnyNotificationsAndDigestsAccess,
		hasAllNotificationsAndDigestsAccess,
		hasAnyLearningProgramsAccess,
		hasAllLearningProgramsManagementAccess,
		hasOnlyLearningProgramsSurveysViewAccess,
		hasConfigurationAccess,
		hasDataDashboardAccess,
		hasHighlightsAccess,
		hasCustomPageAccess,
		hasNotificationSettingsAccess,
		hasDigestManagementAccess,
		hasEventManagementAccess,
		isFreeBeta,
	};
};
